import type { CreditV1 } from '@alpod-ai-writer/backend/src/schemas/credit/credit.schema';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Badge, Button, Card, Spinner } from 'flowbite-react';
import { ArrowDownIcon, ArrowUpIcon, ChevronLeftIcon, ChevronRightIcon, CoinsIcon, PlusIcon, TrendingUpIcon } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { useApi } from '../../../api/useApi.hook';
import { ErrorMessage } from '../../../components/errorMessage/errorMessage.component';
import { clearReturnPath as clearCreditTopUpReturnPath } from '../../../lib/creditTopUpReturn.utils';
import { pathWithParams } from '../../../lib/routing.utils';
import { AppHeader } from '../../components/appHeader/appHeader.component';
import { AppHeaderButton } from '../../components/appHeader/appHeaderButton.component';
//import { DUMMY_EXPENSE_BREAKDOWN } from '../../data/credit.dummyData';
import { useCredits } from '../../hooks/useCredits.hook';
import { RouteIndex } from '../../routeIndex';
import { TransactionType } from '../../types/credit.types';

/**
 * Moje Peněženka (My Wallet) View
 *
 * Displays:
 * - Current credit balance
 * - Transaction history
 * - Expense breakdown by AI feature type
 * - Button to purchase more credits
 */
const PAGE_SIZE = 30;

const BACK_ALLOWED_PATTERNS = [
    /^\/$/, // document list (homepage)
    /^\/document\/[^/]+\/edit/, // document edit
];

const WalletView = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const api = useApi();

    const handleBack = useCallback(() => {
        const from = (location.state as { from?: string })?.from;
        if (from && BACK_ALLOWED_PATTERNS.some(pattern => pattern.test(from))) {
            navigate(-1);
        } else {
            navigate(pathWithParams(RouteIndex.documentList, {}));
        }
    }, [location.state, navigate]);

    const handleTopUp = useCallback(() => {
        // The user is initiating top-up from the wallet itself, so any prior
        // return path (e.g. from a document) is no longer relevant.
        clearCreditTopUpReturnPath();
        navigate(pathWithParams(RouteIndex.topUpCredit, {}));
    }, [navigate]);
    const { balance, isLoading: isLoadingBalance } = useCredits();
    const [page, setPage] = useState(0);

    const { data: fullBalance } = useQuery({
        queryKey: ['wallet-full-balance'],
        queryFn: () => api.v1.credits.getFullBalance(),
    });

    // Fetch transactions from API
    const { data: transactionsData, isLoading: isLoadingTransactions, error: transactionsError } = useQuery({
        queryKey: ['credit-transactions', page],
        queryFn: () => api.v1.credits.getTransactions({ limit: PAGE_SIZE, offset: page * PAGE_SIZE, sort: [['createdAt', 'desc']] }),
    });

    const totalCount = transactionsData?.count ?? 0;
    const totalPages = Math.ceil(totalCount / PAGE_SIZE);
    const hasNextPage = page < totalPages - 1;
    const hasPrevPage = page > 0;

// Sort transactions by date (newest first) and convert to frontend format
    interface TransactionData {
        id: string;
        type: TransactionType;
        credits: number;
        description: string;
        relatedPackageId: string | null;
        relatedDocumentId: string | null;
        createdAt: Date;
    }

    const sortedTransactions = useMemo(() => {
        if (!transactionsData?.items) return [];

        return transactionsData.items.map((transaction: CreditV1.ListItem) => ({
            id: transaction.transactionId,
            type: transaction.type as TransactionType, // TODO: Map backend enum to frontend enum if needed
            credits: transaction.credits,
            description: transaction.description || '',
            createdAt: new Date(transaction.createdAt),
            relatedPackageId: transaction.relatedPackageId,
            relatedDocumentId: transaction.relatedDocumentId,
        }));
    }, [transactionsData]);

    const formatDate = (date: Date) => {
        return new Intl.DateTimeFormat('cs-CZ', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        }).format(date);
    };

    const isLoading = isLoadingBalance || isLoadingTransactions;

    return (
        <>
            <AppHeader pageTitle='Moje Peněženka' onBackClick={handleBack}>
                <AppHeaderButton
                    style='primary'
                    size='lg'
                    onClick={handleTopUp}
                >
                    <PlusIcon size={20} />
                    <span className='hidden lg:block'>Dobít kredit</span>
                </AppHeaderButton>
            </AppHeader>

            <div className='p-4 pt-16 md:pt-20 md:pb-12 flex flex-col items-center'>
                <div className='w-full max-w-6xl'>
                    {/* Loading State */}
                    {isLoading && (
                        <div className='flex justify-center items-center py-12'>
                            <Spinner size='xl' />
                        </div>
                    )}

                    {/* Error State */}
                    {transactionsError && (
                        <div className='text-center py-8'>
                            <p className='text-red-800 font-medium mb-2'>Chyba při načítání transakcí</p>
                            <ErrorMessage>
                                Nepodařilo se načíst historii transakcí. Zkuste to prosím znovu.
                            </ErrorMessage>
                        </div>
                    )}

                    {/* Content */}
                    {!isLoading && (
                        <>
                            {/* Balance Overview */}
                            <div className='grid grid-cols-1 md:grid-cols-3 gap-4 mb-8'>
                                <Card className='bg-gradient-to-br from-primary-500 to-primary-700 text-white'>
                                    <div className='flex flex-col gap-2'>
                                        <div className='flex flex-row items-center gap-2 text-white/80'>
                                            <CoinsIcon size={20} />
                                            <span className='text-sm font-medium'>Dostupné kredity</span>
                                        </div>
                                        <div className='text-4xl font-bold'>
                                            {balance}
                                        </div>
                                    </div>
                                </Card>

                                <Card>
                                    <div className='flex flex-col gap-2'>
                                        <div className='flex flex-row items-center gap-2 text-gray-600'>
                                            <TrendingUpIcon size={20} />
                                            <span className='text-sm font-medium'>Celkově použitých kreditů</span>
                                        </div>
                                        <div className='text-4xl font-bold text-gray-800'>
                                            {fullBalance?.usedCredits ?? 0}
                                        </div>
                                    </div>
                                </Card>

                                <Card>
                                    <div className='flex flex-col gap-2'>
                                        <div className='flex flex-row items-center gap-2 text-gray-600'>
                                            <ArrowUpIcon size={20} />
                                            <span className='text-sm font-medium'>Celkem zakoupených kreditů</span>
                                        </div>
                                        <div className='text-4xl font-bold text-gray-800'>
                                            {fullBalance?.totalSpent ?? 0}
                                        </div>
                                    </div>
                                </Card>
                            </div>

                            {/* Expense Breakdown */}
                            {/*                   <Card className='mb-8'>
                        <h2 className='text-xl font-semibold text-gray-800 mb-4'>Rozpad výdajů podle funkcí</h2>
                        <div className='space-y-4'>
                            {DUMMY_EXPENSE_BREAKDOWN.map((expense) => (
                                <div key={expense.featureType} className='flex flex-col gap-2'>
                                    <div className='flex flex-row items-center justify-between'>
                                        <div className='flex flex-col'>
                                            <span className='font-medium text-gray-800'>{expense.label}</span>
                                            <span className='text-sm text-gray-600'>
                                                {expense.usageCount}× použití
                                            </span>
                                        </div>
                                        <div className='flex flex-row items-center gap-3'>
                                            <span className='text-sm text-gray-600'>
                                                {expense.percentage.toFixed(1)}%
                                            </span>
                                            <span className='font-semibold text-gray-800 min-w-[4rem] text-right'>
                                                {expense.creditsUsed} kr.
                                            </span>
                                        </div>
                                    </div>
                                    <div className='w-full bg-gray-200 rounded-full h-2'>
                                        <div
                                            className='bg-primary-500 h-2 rounded-full transition-all duration-300'
                                            style={{ width: `${expense.percentage}%` }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Card>*}

                            {/* Transaction History */}
                            <Card>
                                <div className='flex flex-row items-center justify-between mb-4'>
                                    <h2 className='text-xl font-semibold text-gray-800'>Historie transakcí</h2>
                                    <Badge color='gray' size='sm'>
                                        {totalCount} transakcí
                                    </Badge>
                                </div>

                                <div className='space-y-3'>
                                    {sortedTransactions.map((transaction: TransactionData) => (
                                        <div
                                            key={transaction.id}
                                            className={clsx(
                                                'flex flex-col md:flex-row md:items-center justify-between p-4 rounded-lg border',
                                                transaction.type === TransactionType.CREDIT_PURCHASE && 'bg-green-50 border-green-200',
                                                transaction.type === TransactionType.CREDIT_USAGE && 'bg-gray-50 border-gray-200',
                                                transaction.type === TransactionType.CREDIT_REFUND && 'bg-blue-50 border-blue-200'
                                            )}
                                        >
                                            <div className='flex flex-row items-start gap-3 mb-2 md:mb-0'>
                                                <div className={clsx(
                                                    'p-2 rounded-full',
                                                    transaction.type === TransactionType.CREDIT_PURCHASE && 'bg-green-200',
                                                    transaction.type === TransactionType.CREDIT_USAGE && 'bg-gray-200',
                                                    transaction.type === TransactionType.CREDIT_REFUND && 'bg-blue-200'
                                                )}>
                                                    {transaction.type === TransactionType.CREDIT_USAGE && (
                                                        <ArrowDownIcon size={20} className='text-gray-700' />
                                                    )}
                                                    {transaction.type === TransactionType.CREDIT_PURCHASE && (
                                                        <ArrowUpIcon size={20} className='text-green-700' />
                                                    )}
                                                    {transaction.type === TransactionType.CREDIT_REFUND && (
                                                        <ArrowDownIcon size={20} className='text-blue-700' />
                                                    )}
                                                </div>
                                                <div className='flex flex-col'>
                                                    <span className='font-medium text-gray-800'>
                                                        {transaction.type === TransactionType.CREDIT_USAGE && (
                                                            <p>Použití kreditů</p>
                                                        )}
                                                        {transaction.type === TransactionType.CREDIT_PURCHASE && (
                                                            <p>Nákup kreditů</p>
                                                        )}
                                                        {transaction.type === TransactionType.CREDIT_REFUND && (
                                                            <p>Vrácení kreditů</p>
                                                        )}
                                                    </span>
                                                    <span className='text-sm text-gray-600'>
                                                        {formatDate(transaction.createdAt)}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className='flex flex-row items-center gap-4 md:ml-4'>
                                                <span className={clsx(
                                                    'font-semibold min-w-[5rem] text-right',
                                                    transaction.credits > 0 && 'text-green-700',
                                                    transaction.credits < 0 && 'text-red-700'
                                                )}>
                                                    {transaction.credits > 0 ? '+' : ''}{transaction.credits} kredit
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {totalPages > 1 && (
                                    <div className='flex items-center justify-center gap-4 mt-4 pt-4 border-t border-gray-200'>
                                        <Button
                                            color='gray'
                                            size='sm'
                                            disabled={!hasPrevPage || isLoadingTransactions}
                                            onClick={() => setPage((p) => p - 1)}
                                        >
                                            <ChevronLeftIcon size={16} className='mr-1' />
                                            Novější
                                        </Button>
                                        <span className='text-sm text-gray-600'>
                                            Stránka {page + 1} z {totalPages}
                                        </span>
                                        <Button
                                            color='gray'
                                            size='sm'
                                            disabled={!hasNextPage || isLoadingTransactions}
                                            onClick={() => setPage((p) => p + 1)}
                                        >
                                            Starší
                                            <ChevronRightIcon size={16} className='ml-1' />
                                        </Button>
                                    </div>
                                )}

                                {sortedTransactions.length === 0 && (
                                    <div className='flex flex-col items-center justify-center py-12 text-gray-500'>
                                        <CoinsIcon size={48} className='mb-4 opacity-30' />
                                        <p className='text-lg font-medium'>Zatím žádné transakce</p>
                                        <p className='text-sm'>Začněte nákupem kreditů</p>
                                        <Button
                                            color='primary'
                                            size='lg'
                                            className='mt-4'
                                            onClick={handleTopUp}
                                        >
                                    Dobít kredit
                                        </Button>
                                    </div>
                                )}
                            </Card>

                            {/* Call to Action */}
                            {sortedTransactions.length > 0 && (
                                <div className='mt-8 flex flex-col items-center gap-4 p-6 bg-gradient-to-r from-primary-50 to-primary-100 rounded-lg'>
                                    <p className='text-lg font-medium text-gray-800 text-center'>
                                        Potřebujete více kreditů?
                                    </p>
                                    <Button
                                        color='primary'
                                        size='lg'
                                        onClick={handleTopUp}
                                    >
                                        <PlusIcon size={20} className='mr-2' />
                                        Dobít kredit
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default WalletView;

